/**
 * <count-up-group> finds every [data-count-value] element inside it and
 * animates each from 0 up to its target the first time the group scrolls
 * into view — a numeric counterpart to <reveal-on-scroll>.
 *
 * A value can carry a trailing non-digit suffix (e.g. "150+"): the digits
 * animate, the suffix is appended as static text once counting finishes.
 */

const OBSERVER_OPTIONS = {
  threshold: 0.4,
};

const STAGGER_MS = 120;
const DURATION_MS = 1800;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function animateCount(el, target, suffix) {
  const start = performance.now();

  function step(now) {
    const progress = Math.min((now - start) / DURATION_MS, 1);
    const current = Math.round(target * easeOutCubic(progress));
    el.textContent = current + suffix;

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

class CountUpGroup extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const targets = Array.from(this.querySelectorAll('[data-count-value]'))
      .map((el) => {
        const raw = (el.getAttribute('data-count-value') || '').trim();
        const match = raw.match(/^(\d+)(.*)$/);
        if (!match) return null;
        return { el, target: parseInt(match[1], 10), suffix: match[2] };
      })
      .filter((item) => item !== null);

    if (targets.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(({ el, target, suffix }) => {
        el.textContent = target + suffix;
      });
      return;
    }

    targets.forEach(({ el }) => {
      el.textContent = '0';
    });

    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        this.#observer?.disconnect();

        targets.forEach(({ el, target, suffix }, index) => {
          window.setTimeout(() => animateCount(el, target, suffix), index * STAGGER_MS);
        });
      });
    }, OBSERVER_OPTIONS);

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('count-up-group')) {
  customElements.define('count-up-group', CountUpGroup);
}
